"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Scissors, Loader2 } from "lucide-react"
import type { SplitOptions, ValidationErrors } from "./types"

interface SplitOptionsFormProps {
  splitOptions: SplitOptions
  onSplitOptionsChange: (updater: (prev: SplitOptions) => SplitOptions) => void
  validationErrors: ValidationErrors
  hasValidationErrors: boolean
  isProcessing: boolean
  isUploadReady: boolean
  onSplit: () => void
}

export function SplitOptionsForm({
  splitOptions,
  onSplitOptionsChange,
  validationErrors,
  hasValidationErrors,
  isProcessing,
  isUploadReady,
  onSplit,
}: SplitOptionsFormProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Split Mode</Label>
        <Select
          value={splitOptions.mode}
          onValueChange={(value) =>
            onSplitOptionsChange((prev) => ({
              ...prev,
              mode: value as SplitOptions["mode"],
            }))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="by-page-count">By Page Count</SelectItem>
            <SelectItem value="by-file-size">By File Size</SelectItem>
            <SelectItem value="custom-ranges">Custom Ranges</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {splitOptions.mode === "by-page-count" && (
        <>
          <div className="space-y-2">
            <Label className={validationErrors.pagesPerChunk ? "text-destructive" : ""}>
              Pages per Chunk (5-200)
            </Label>
            <Input
              type="number"
              value={splitOptions.pagesPerChunk ?? ""}
              onChange={(e) => {
                const val = e.target.value === "" ? undefined : e.target.valueAsNumber;
                onSplitOptionsChange((prev) => ({
                  ...prev,
                  pagesPerChunk: val as number,
                }));
              }}
              className={validationErrors.pagesPerChunk ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {validationErrors.pagesPerChunk ? (
              <p className="text-xs text-destructive font-medium">
                {validationErrors.pagesPerChunk}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Recommended: 50 pages for Claude, 100 pages for GPT-4
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className={validationErrors.overlapPages ? "text-destructive" : ""}>
              Overlap Pages (0-10)
            </Label>
            <Input
              type="number"
              value={splitOptions.overlapPages ?? ""}
              onChange={(e) => {
                const val = e.target.value === "" ? undefined : e.target.valueAsNumber;
                onSplitOptionsChange((prev) => ({
                  ...prev,
                  overlapPages: val as number,
                }));
              }}
              className={validationErrors.overlapPages ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {validationErrors.overlapPages ? (
              <p className="text-xs text-destructive font-medium">
                {validationErrors.overlapPages}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Include pages from previous chunk for context continuity
              </p>
            )}
          </div>
        </>
      )}

      {splitOptions.mode === "by-file-size" && (
        <div className="space-y-2">
          <Label className={validationErrors.maxFileSizeMB ? "text-destructive" : ""}>
            Maximum File Size per Chunk (1-100 MB)
          </Label>
          <Input
            type="number"
            value={splitOptions.maxFileSizeMB ?? ""}
            onChange={(e) => {
              const val = e.target.value === "" ? undefined : e.target.valueAsNumber;
              onSplitOptionsChange((prev) => ({
                ...prev,
                maxFileSizeMB: val as number,
              }));
            }}
            className={validationErrors.maxFileSizeMB ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          {validationErrors.maxFileSizeMB ? (
            <p className="text-xs text-destructive font-medium">
              {validationErrors.maxFileSizeMB}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Split by file size to stay within API limits
            </p>
          )}
        </div>
      )}

      {splitOptions.mode === "custom-ranges" && (
        <div className="space-y-2">
          <Label>Page Ranges</Label>
          <Input
            type="text"
            placeholder="e.g., 1-10, 11-20, 21-30"
            value={splitOptions.customRanges}
            onChange={(e) =>
              onSplitOptionsChange((prev) => ({
                ...prev,
                customRanges: e.target.value,
              }))
            }
          />
          <p className="text-xs text-muted-foreground">
            Specify custom page ranges separated by commas
          </p>
        </div>
      )}

      <Button
        onClick={onSplit}
        disabled={isProcessing || !isUploadReady || hasValidationErrors}
        className="w-full"
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Splitting PDF...
          </>
        ) : (
          <>
            <Scissors className="h-4 w-4 mr-2" />
            Split PDF
          </>
        )}
      </Button>
    </div>
  )
}
